import PolicyLayout, { PolicySection, P, UL, OL, LI, Callout } from "./PolicyLayout";

const TOC = [
  { id: "acceptance", label: "Acceptance" },
  { id: "who-we-are", label: "Who we are" },
  { id: "accounts", label: "Accounts" },
  { id: "clients", label: "Booking as a client" },
  { id: "coaches", label: "Working as a coach" },
  { id: "payments", label: "Payments & fees" },
  { id: "cancellations", label: "Cancellations & refunds" },
  { id: "conduct", label: "Acceptable use" },
  { id: "ip", label: "Content & IP" },
  { id: "warranty", label: "No warranty" },
  { id: "liability", label: "Liability" },
  { id: "changes", label: "Changes to these terms" },
  { id: "governing", label: "Governing law" },
  { id: "contact", label: "Contact" },
];

export default function TermsOfService() {
  return (
    <PolicyLayout
      eyebrow="Terms"
      title="Terms of Service"
      updated="February 2026"
      intro="Coachly is a marketplace that connects independent coaches with clients. These terms explain what you can expect from us and what we expect from you when you use the platform."
      toc={TOC}
      testId="policy-tos"
    >
      <PolicySection id="acceptance" title="1. Acceptance of these terms">
        <P>
          By creating an account, browsing the site, joining our waitlist, applying to
          become a coach, booking a session, or otherwise using Coachly (the “Service”),
          you agree to these Terms of Service (the “Terms”) and the policies referenced
          in them, including our{" "}
          <a href="/privacy" className="text-[#2563EB] hover:underline">Privacy Policy</a>,{" "}
          <a href="/refund-policy" className="text-[#2563EB] hover:underline">Refund &amp; Cancellation Policy</a>,{" "}
          <a href="/coach-guidelines" className="text-[#2563EB] hover:underline">Coach Guidelines</a>, and{" "}
          <a href="/trust-safety" className="text-[#2563EB] hover:underline">Trust &amp; Safety Policy</a>.
        </P>
        <P>
          If you do not agree, please do not use the Service.
        </P>
      </PolicySection>

      <PolicySection id="who-we-are" title="2. Who we are">
        <P>
          Coachly is operated by Coachly, Inc. (“Coachly,” “we,” “us,” or “our”),
          a Delaware corporation with a mailing address in the United States. You can
          reach us any time at{" "}
          <a href="mailto:support@coachly.app" className="text-[#2563EB] hover:underline">support@coachly.app</a>.
        </P>
        <P>
          Coachly is a marketplace only. Coaches on Coachly are independent
          professionals — <strong>not employees or agents of Coachly</strong>. We do
          not deliver coaching services ourselves; we make it easier to find, book,
          and pay a coach you choose.
        </P>
      </PolicySection>

      <PolicySection id="accounts" title="3. Accounts">
        <UL>
          <LI>You must be at least 18 years old to create an account or book a coach.</LI>
          <LI>You are responsible for the accuracy of the information you provide, including your legal name, email, and, for coaches, professional credentials.</LI>
          <LI>You are responsible for anything that happens under your account. Keep your login secure and let us know right away if you suspect unauthorized use.</LI>
          <LI>We may suspend or close accounts that violate these Terms, our Trust &amp; Safety Policy, or applicable law.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="clients" title="4. Booking as a client">
        <P>
          When you book a session on Coachly, you enter into a contract for coaching
          services directly with the coach you selected. Coachly facilitates that
          booking and processes the payment, but is not a party to your coaching
          contract.
        </P>
        <UL>
          <LI>You must show up on time and follow the coach's reasonable session guidance.</LI>
          <LI>You are responsible for making sure any activity you undertake is safe for you. Consult a doctor before beginning any new physical training.</LI>
          <LI>You agree to communicate respectfully. See our{" "}
            <a href="/trust-safety" className="text-[#2563EB] hover:underline">Trust &amp; Safety Policy</a> for what that means in practice.
          </LI>
        </UL>
      </PolicySection>

      <PolicySection id="coaches" title="5. Working as a coach">
        <P>
          If your coach application is approved, you can offer paid sessions on
          Coachly. Full expectations for coaches are in our{" "}
          <a href="/coach-guidelines" className="text-[#2563EB] hover:underline">Coach Guidelines</a>.
          A few things to highlight here:
        </P>
        <UL>
          <LI>You are an independent contractor. Nothing in these Terms creates an employment or agency relationship with Coachly.</LI>
          <LI>You are responsible for your own taxes, insurance, licenses, and any professional certifications you claim.</LI>
          <LI>You set your own price, availability, and cancellation window on your Coachly profile.</LI>
          <LI>You must have the right to sell the coaching service you're offering (e.g. no unauthorized use of a copyrighted program).</LI>
        </UL>
      </PolicySection>

      <PolicySection id="payments" title="6. Payments &amp; fees">
        <P>
          Coachly uses <strong>Stripe</strong> to process card payments and to send
          payouts to coaches via Stripe Connect. When you pay for a session, Coachly
          holds the funds until the session is marked complete, then releases the
          coach's share automatically minus a platform service fee.
        </P>
        <UL>
          <LI>Prices are shown in the currency displayed at checkout (currently US dollars). Applicable sales tax may apply.</LI>
          <LI>Coachly's platform fee is 10% of the session price unless we've told you otherwise in writing.</LI>
          <LI>Payments and disputes are subject to Stripe's terms in addition to ours.</LI>
        </UL>
        <Callout>
          Full payout timing, refund windows, and no-show handling are covered in the{" "}
          <a href="/refund-policy" className="underline">Refund &amp; Cancellation Policy</a>.
        </Callout>
      </PolicySection>

      <PolicySection id="cancellations" title="7. Cancellations &amp; refunds">
        <P>
          Both clients and coaches can cancel bookings, subject to the timing rules in
          our{" "}
          <a href="/refund-policy" className="text-[#2563EB] hover:underline">Refund &amp; Cancellation Policy</a>.
          In brief: free client cancellation up to 24 hours before the session, 50% fee within 24 hours,
          full charge for no-shows. Coach-initiated cancellations are always refunded in full.
        </P>
      </PolicySection>

      <PolicySection id="conduct" title="8. Acceptable use">
        <P>You agree not to use Coachly to:</P>
        <UL>
          <LI>Break any law or infringe anyone's rights.</LI>
          <LI>Harass, threaten, discriminate against, or endanger another user.</LI>
          <LI>Impersonate someone else or misrepresent your credentials.</LI>
          <LI>Circumvent Coachly's payment system, e.g. by trying to move a booked client off-platform to avoid fees.</LI>
          <LI>Interfere with the Service, scrape it at scale, or reverse-engineer it.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="ip" title="9. Content &amp; intellectual property">
        <P>
          Coachly and its logos, look and feel, and code are ours (or our licensors').
          You retain ownership of the content you post — profile bios, reviews, session
          notes, chat messages — but you grant Coachly a non-exclusive, worldwide,
          royalty-free license to host, display, and promote that content as needed to
          run the Service. You warrant you have the right to grant that license.
        </P>
      </PolicySection>

      <PolicySection id="warranty" title="10. No warranty on coaching outcomes">
        <P>
          The Service is provided <strong>“as is”</strong> and <strong>“as available.”</strong>{" "}
          Coachly does not guarantee any specific outcome from a coaching session, that a
          coach's advice is medically or legally appropriate for you, or that the Service
          will always be uninterrupted or error-free. To the fullest extent permitted by
          law, we disclaim all implied warranties, including merchantability and fitness
          for a particular purpose.
        </P>
      </PolicySection>

      <PolicySection id="liability" title="11. Limitation of liability">
        <P>
          To the fullest extent permitted by law, Coachly, its officers, employees, and
          affiliates will not be liable for any indirect, incidental, special, consequential,
          or punitive damages, or for any loss of profits, data, or business, arising out
          of your use of the Service. Our total liability to you for any claim relating to
          the Service is limited to the greater of (a) the amount you paid Coachly in the
          six months preceding the claim or (b) US $100.
        </P>
        <P>
          Some U.S. states do not allow limits on implied warranties or certain damages, so
          parts of this section may not apply to you.
        </P>
      </PolicySection>

      <PolicySection id="changes" title="12. Changes to these terms">
        <P>
          We may update these Terms from time to time. If a change is material we'll email
          you and post a notice on the site at least 14 days before it takes effect. Your
          continued use of the Service after that time means you accept the update.
        </P>
      </PolicySection>

      <PolicySection id="governing" title="13. Governing law &amp; disputes">
        <P>
          These Terms are governed by the laws of the State of Delaware, without regard to
          its conflict-of-law rules. Any dispute that isn't resolved informally will be
          brought in the state or federal courts located in Delaware. You and Coachly each
          waive the right to a jury trial and to participate in a class action, to the
          extent permitted by law.
        </P>
      </PolicySection>

      <PolicySection id="contact" title="14. Contact">
        <P>
          Questions about these Terms? Write to{" "}
          <a href="mailto:legal@coachly.app" className="text-[#2563EB] hover:underline">legal@coachly.app</a>{" "}
          or{" "}
          <a href="mailto:support@coachly.app" className="text-[#2563EB] hover:underline">support@coachly.app</a>.
        </P>
      </PolicySection>
    </PolicyLayout>
  );
}
